import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, Clock, MapPin, Video, Plus, Edit, Trash2, Bell, GripVertical, List } from 'lucide-react';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { EVENT_COLORS, WEEKDAYS, DraggableEvent, DroppableDay, formatTime, formatDate } from './constants';

function renderCompactEvent(event, openEditModal) {
  const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
  return (
    <DraggableEvent key={event.id} event={event}>
      <div
        onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
        className="px-1.5 py-0.5 rounded text-xs cursor-pointer hover:opacity-80 truncate mb-0.5 flex items-center gap-1 group"
        style={{ backgroundColor: config.light, color: config.text, borderLeft: `2px solid ${config.bg}` }}
        title={`${event.title} - Glisser pour déplacer`}
      >
        <GripVertical className="h-3 w-3 opacity-0 group-hover:opacity-50 flex-shrink-0" />
        <span className="font-medium">{formatTime(event.start_datetime)}</span>
        <span className="ml-1 truncate">{event.title}</span>
      </div>
    </DraggableEvent>
  );
}

export function MonthView({ sensors, draggedEvent, handleDragStart, handleDragEnd, monthDays, getEventsForDay, selectedDay, openDayDetail, openEditModal }) {
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((day) => (
              <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map(({ date, isCurrentMonth }) => {
              const dayEvents = getEventsForDay(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isSelected = selectedDay?.toDateString() === date.toDateString();
              const dateStr = date.toISOString().split('T')[0];
              return (
                <DroppableDay key={dateStr} date={date}>
                  <div
                    onClick={() => openDayDetail(date)}
                    className={`min-h-[100px] p-1 border rounded-lg cursor-pointer transition-all hover:border-blue-300 ${
                      isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${isToday ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200'} ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <div className={`text-right text-sm mb-1 ${isToday ? 'font-bold text-blue-600' : isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-0.5 max-h-[70px] overflow-hidden">
                      {dayEvents.slice(0, 3).map(event => renderCompactEvent(event, openEditModal))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 text-center font-medium">+{dayEvents.length - 3} autres</div>
                      )}
                    </div>
                  </div>
                </DroppableDay>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <DragOverlay>
        {draggedEvent && (
          <div className="px-2 py-1 rounded text-xs bg-blue-500 text-white shadow-lg">
            <span className="font-medium">{formatTime(draggedEvent.start_datetime)}</span>
            <span className="ml-1">{draggedEvent.title}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export function WeekView({ weekDays, getEventsForDay, openEditModal }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div key={index} className={`min-h-[300px] border rounded-lg overflow-hidden ${isToday ? 'ring-2 ring-blue-500' : ''}`}>
                <div className={`text-center py-2 border-b ${isToday ? 'bg-blue-600 text-white' : 'bg-gray-50'}`}>
                  <div className="text-xs opacity-75">{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                  <div className="text-lg font-bold">{day.getDate()}</div>
                </div>
                <div className="p-2 space-y-1 overflow-y-auto max-h-[250px]">
                  {dayEvents.map((event) => {
                    const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
                    return (
                      <div key={event.id} onClick={() => openEditModal(event)}
                        className="p-2 rounded cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: config.light, borderLeft: `3px solid ${config.bg}` }}>
                        <div className="font-medium text-sm truncate" style={{ color: config.text }}>{event.title}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />{formatTime(event.start_datetime)}
                        </div>
                        {event.location && (
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                            <MapPin className="h-3 w-3" />{event.location}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {dayEvents.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Aucun événement</p>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function DayView({ currentDate, dayEvents, openCreateModal, openEditModal, handleDeleteEvent, user }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          {dayEvents.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucun événement prévu ce jour</p>
              <Button onClick={() => openCreateModal(currentDate)} className="mt-4">
                <Plus className="h-4 w-4 mr-1" />Ajouter un événement
              </Button>
            </div>
          ) : (
            dayEvents.map((event) => {
              const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
              const isOrganizer = event.organizer_id === user?.id;
              return (
                <div key={event.id} className="flex border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="w-2" style={{ backgroundColor: config.bg }} />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-lg">{event.title}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
                          {event.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{event.location}</span>}
                          {event.is_online && <Badge className="bg-sky-800 text-white font-medium"><Video className="h-3 w-3 mr-1" />En ligne</Badge>}
                        </div>
                        {event.description && <p className="text-sm text-gray-500 mt-2">{event.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge style={{ backgroundColor: config.bg }} className="text-white">{config.label}</Badge>
                        {isOrganizer && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEditModal(event)}><Edit className="h-4 w-4" /></Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteEvent(event.id)}><Trash2 className="h-4 w-4" /></Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ListView({ filteredEvents, openEditModal, handleDeleteEvent, user }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><List className="h-5 w-5" />Tous les événements</CardTitle>
      </CardHeader>
      <CardContent>
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucun événement trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Événement</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Heure</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Lieu</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredEvents.map((event) => {
                  const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
                  const isOrganizer = event.organizer_id === user?.id;
                  return (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{event.title}</div>
                        {event.description && <div className="text-xs text-gray-500 truncate max-w-[200px]">{event.description}</div>}
                      </td>
                      <td className="px-4 py-3"><Badge style={{ backgroundColor: config.bg }} className="text-white">{config.label}</Badge></td>
                      <td className="px-4 py-3 text-sm">{formatDate(event.start_datetime)}</td>
                      <td className="px-4 py-3 text-sm">{formatTime(event.start_datetime)}</td>
                      <td className="px-4 py-3 text-sm">
                        {event.is_online ? (
                          <Badge className="bg-sky-800 text-white font-medium"><Video className="h-3 w-3 mr-1" />En ligne</Badge>
                        ) : (event.location || '-')}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditModal(event)}><Edit className="h-4 w-4" /></Button>
                          {isOrganizer && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteEvent(event.id)}><Trash2 className="h-4 w-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UpcomingEvents({ upcoming, openEditModal, user }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="h-5 w-5 text-orange-500" />Prochains événements
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {upcoming.slice(0, 6).map((event) => {
            const config = EVENT_COLORS[event.event_type] || EVENT_COLORS.other;
            const isOrganizer = event.organizer_id === user?.id;
            return (
              <div key={event.id} className="p-3 border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => openEditModal(event)}>
                <div className="flex items-start gap-3">
                  <div className="w-1 h-full min-h-[60px] rounded-full flex-shrink-0" style={{ backgroundColor: config.bg }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{event.title}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                      <Clock className="h-3 w-3" />{formatDate(event.start_datetime)} à {formatTime(event.start_datetime)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge style={{ backgroundColor: config.bg }} className="text-white text-xs">{config.label}</Badge>
                      {isOrganizer && <Badge className="bg-blue-800 text-white text-xs font-medium">Organisateur</Badge>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
